import cloneDeep from 'lodash/cloneDeep'

import { defaultSelectedTab } from '../../../constants/constants'
import { common } from '../common-filter-data'

import {
  SET_INITIAL_FILTER_INFO,
  SET_CURRENT_FILTER_INFO,
  UPDATE_STOPS_FILTERS_REQ,
  UPDATE_AIRLINE_FILTERS_REQ,
  UPDATE_ATTRIBUTES_FILTERS_REQ,
  UPDATE_TIME_FILTERS,
  RESET_TIME_FILTERS,
  UPDATE_AIRPORTS_FILTERS_REQ,
  UPDATE_EXACT_DATE_FILTERS_REQ,
  UPDATE_INCLUDED_CARRIERS,
} from '../../../actions/actionTypes'
import { getMaxSelectedStops } from './helpers/getMaxSelectedStops'
import { incrementOneDay } from './helpers/incrementOneDay'
import { pclnExpt } from '../../../../common/fly-experimentation/fly-experimentation'

/*
This reducer function takes in the data that comes from the individual filters, which in turn
comes from the @pcln-filter common filter component.  The individual filter reducers (stopsFilters, airlineFilters, etc)
keep the data in the format the FilterComponent needs.

The AirSearchRequest however needs the data transformed into an object of a different shape
(state.filterInfo.values) and this reducer handles this transformation and state updates.
*/

/* META - FILTER-INFO */
export function filterInfo(state = {}, action) {
  const { values: prevValues, sliceFilters: prevSliceFilters } = state
  const { payload = {}, entity, meta = {}, sbsParams = {} } = action
  const { isNoneClick, isAllClick, isOnlyClick } = meta
  let latestFilterChange = {}
  const sliceIndex = sbsParams.activeTabId || defaultSelectedTab
  const isTimeChipVariant = pclnExpt.isVariant(
    'AIR_LISTINGS_FILTER_REDESIGN_TIME'
  )
  switch (action.type) {
    case UPDATE_AIRLINE_FILTERS_REQ:
      const { airlines } = action
      const changedValue = payload && payload.value
      const changedChecked = payload && payload.checked
      const includedCarriers = []
      const excludedCarriers = []

      airlines.forEach(airline => {
        const { value, checked } = airline
        if (isAllClick || (isOnlyClick && value === changedValue)) {
          includedCarriers.push(value)
        } else if (isNoneClick || (isOnlyClick && value !== changedValue)) {
          excludedCarriers.push(value)
        } else if (value === changedValue) {
          if (changedChecked) {
            includedCarriers.push(value)
          } else {
            excludedCarriers.push(value)
          }
        } else {
          if (checked) {
            includedCarriers.push(value)
          } else {
            excludedCarriers.push(value)
          }
        }
      })

      if (sbsParams.isSBS) {
        const sliceIndex = sbsParams.activeTabId || defaultSelectedTab

        latestFilterChange = {
          ['includedCarriers' + sliceIndex]: includedCarriers,
          ['excludedCarriers' + sliceIndex]: excludedCarriers,
        }

        // TODO: Use 'sliceFilters' to build filter object for various slices
        const sliceFilters = prevSliceFilters.map((slice, index) => {
          if (index === sliceIndex) {
            if (excludedCarriers.length) {
              slice.excludedCarriers = excludedCarriers
              slice.includedCarriers = includedCarriers
            } else {
              delete slice.excludedCarriers
              delete slice.includedCarriers
            }
          }

          return slice
        })

        const updatedAirlineValues = Object.assign(
          {},
          prevValues,
          latestFilterChange
        )

        return {
          ...state,
          latestFilterChange,
          sliceFilters,
          values: updatedAirlineValues,
        }
      }

      latestFilterChange = {
        includedCarriers,
        excludedCarriers,
      }

      const sliceFilters = prevSliceFilters.map(slice => {
        if (excludedCarriers.length) {
          slice.excludedCarriers = excludedCarriers
          slice.includedCarriers = includedCarriers
        } else {
          delete slice.excludedCarriers
          delete slice.includedCarriers
        }
        return slice
      })

      const updatedAirlineValues = Object.assign(
        {},
        prevValues,
        latestFilterChange
      )

      return {
        ...state,
        latestFilterChange,
        sliceFilters,
        values: updatedAirlineValues,
      }

    case UPDATE_ATTRIBUTES_FILTERS_REQ:
      const { attributes = [] } = action
      const changedAttrValue = payload?.value
      const changedAttrChecked = payload.checked
      const includedAttributes = []

      attributes.forEach(attribute => {
        const { value, checked } = attribute
        if (
          isAllClick ||
          (isOnlyClick && value === changedAttrValue) ||
          (value === changedAttrValue && changedAttrChecked) ||
          checked
        ) {
          includedAttributes.push(value)
        }
      })

      const attrSliceFilters = prevSliceFilters.map(slice => {
        slice.includedAttributes = includedAttributes
        return slice
      })

      latestFilterChange = {
        ['attributes' + sliceIndex]: includedAttributes,
      }

      const updatedAttributeValues = Object.assign(
        {},
        prevValues,
        latestFilterChange
      )

      return {
        ...state,
        latestFilterChange,
        sliceFilters: attrSliceFilters,
        values: updatedAttributeValues,
      }

    case UPDATE_STOPS_FILTERS_REQ:
      if (sbsParams.isSBS) {
        const sliceIndex = sbsParams.activeTabId || defaultSelectedTab
        const selectedStops = 'selectedStops' + sliceIndex

        if (meta.isNoneClick) {
          // Set all selected options to false in preparation for subsequent selections to represent that
          // each possible value is un-checked. This accounts for user clicking 'none' before any other stop filters.
          state.values[selectedStops] = Object.keys(
            state.values[selectedStops]
          ).reduce((acc, key) => {
            acc[key] = false
            return acc
          }, {})

          return state
        }

        let stopsSelected = Object.assign(
          {},
          state.values[selectedStops],
          payload
        )

        const numOfStops = getMaxSelectedStops(
          state.values[selectedStops],
          payload,
          action.meta
        )

        if (action.meta && action.meta.isOnlyClick) {
          stopsSelected = { 0: false, 1: false, 7: false }
          stopsSelected[numOfStops] = true
        }

        const stopsKey = 'numOfStops' + sliceIndex
        return {
          ...state,
          latestFilterChange: { [stopsKey]: numOfStops },
          values: Object.assign(
            {},
            state.values,
            { [stopsKey]: numOfStops },
            { [selectedStops]: stopsSelected }
          ),
        }
      }

      if (meta.isNoneClick) {
        // Set all selected options to false in preparation for subsequent selections to represent that
        // each possible value is un-checked. This accounts for user clicking 'none' before any other stop filters.
        state.values.selectedStops = Object.keys(
          state.values.selectedStops
        ).reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})

        return state
      }

      let selectedStops = Object.assign({}, state.values.selectedStops, payload)

      const numOfStops = getMaxSelectedStops(
        state.values.selectedStops,
        payload,
        action.meta
      )

      if (action.meta && action.meta.isOnlyClick) {
        selectedStops = { 0: false, 1: false, 7: false }
        selectedStops[numOfStops] = true
      }

      return {
        ...state,
        latestFilterChange: { numOfStops },
        values: Object.assign(
          {},
          state.values,
          { numOfStops },
          { selectedStops }
        ),
      }

    case RESET_TIME_FILTERS:
      const values = cloneDeep(state.values)
      if (entity === 'duration') {
        state.defaults.TIME.forEach(slice => {
          values[`sliceDurationMax${slice.sliceRefId}`] = ''
          latestFilterChange[`sliceDurationMax${slice.sliceRefId}`] = ''
        })
      } else {
        state.defaults.TIME.forEach(slice => {
          values[`arriveTimeStart${slice.sliceRefId}`] = ''
          values[`arriveTimeEnd${slice.sliceRefId}`] = ''
          values[`departTimeStart${slice.sliceRefId}`] = ''
          values[`departTimeEnd${slice.sliceRefId}`] = ''
          latestFilterChange[`arriveTimeStart${slice.sliceRefId}`] = ''
          latestFilterChange[`arriveTimeEnd${slice.sliceRefId}`] = ''
          latestFilterChange[`departTimeStart${slice.sliceRefId}`] = ''
          latestFilterChange[`departTimeEnd${slice.sliceRefId}`] = ''

          // For new time filters
          if (isTimeChipVariant) {
            values[`${entity}TimeRange`] = []
            latestFilterChange[`${entity}TimeRange`] = []
          }
        })
      }
      state.lastReset = Date.now()
      return {
        ...state,
        values,
        latestFilterChange,
      }

    case UPDATE_TIME_FILTERS:
      /**
       * This reducer is duplicated in timeFilter.js, where it updates the state for UI changes.
       * Here, it updates the state used to create the search req after time filter applied.
       */
      if (!state.values) {
        return state
      }
      latestFilterChange = {}
      const { sliceRefId } = action
      let timeRange = []

      if (entity === 'duration') {
        const newDuration = Math.round(payload.duration / 60 / 1000)

        if (sbsParams.isSBS) {
          latestFilterChange[`sliceDurationMax${sliceRefId}`] = newDuration
        } else {
          prevSliceFilters.forEach(slice => {
            latestFilterChange[`sliceDurationMax${slice.sliceId}`] = newDuration
          })
        }
      } else {
        const { min, max, currSelected, isTimeChipVariant } = payload

        if (isTimeChipVariant && currSelected !== {}) {
          Object.values(currSelected).forEach(val => timeRange.push(val))

          if (entity === 'arrive') {
            //next day arrival logic
            Object.values(currSelected).forEach(val =>
              timeRange.push({
                start: incrementOneDay(val.start),
                end: incrementOneDay(val.end),
              })
            )
          }
        }

        const defaultsName = entity === 'depart' ? 'Takeoff' : 'Landing'
        const originMin = state.defaults?.TIME?.[0]?.[`min${defaultsName}Time`]
        const originMax = state.defaults?.TIME?.[0]?.[`max${defaultsName}Time`]
        // requires to reset the filter chips
        if (min === originMin && max === originMax) {
          state.lastReset = Date.now()
          latestFilterChange[`${entity}TimeStart${sliceRefId}`] = ''
          latestFilterChange[`${entity}TimeEnd${sliceRefId}`] = ''
        } else {
          latestFilterChange[`${entity}TimeStart${sliceRefId}`] = min
          latestFilterChange[`${entity}TimeEnd${sliceRefId}`] = max
        }
      }
      return {
        ...state,
        latestFilterChange,
        values: Object.assign({}, prevValues, latestFilterChange, {
          [`${entity}TimeRange`]: timeRange,
        }),
      }

    case UPDATE_AIRPORTS_FILTERS_REQ:
      latestFilterChange = {}
      const updatedAirportValues = cloneDeep(state.values)
      const [airportCode, shouldAdd] = payload
      const sliceLength = prevSliceFilters.length

      if (sbsParams.isSBS) {
        const i = sbsParams.activeTabId
        if (
          updatedAirportValues[`origAirports${i}`]?.length ===
            state.defaults?.AIRPORTS[0].originAirports.length &&
          updatedAirportValues[`destAirports${i}`]?.length ===
            state.defaults?.AIRPORTS[0].destinationAirports.length
        ) {
          updatedAirportValues[`origAirports${i}`] = []
          updatedAirportValues[`destAirports${i}`] = []
        }
        const originSet = new Set(updatedAirportValues[`origAirports${i}`])
        const destSet = new Set(updatedAirportValues[`destAirports${i}`])
        const activeSet = action.entity === 'origin' ? originSet : destSet
        if (meta.isOnlyClick) {
          activeSet.clear()
          activeSet.add(airportCode)
        } else {
          if (shouldAdd) {
            activeSet.add(airportCode)
          } else {
            activeSet.delete(airportCode)
          }
        }

        updatedAirportValues[`origAirports${i}`] = [...originSet.values()]
        latestFilterChange[`origAirports${i}`] = [...originSet.values()]
        updatedAirportValues[`destAirports${i}`] = [...destSet.values()]
        latestFilterChange[`destAirports${i}`] = [...destSet.values()]
      } else {
        for (let i = 1; i <= sliceLength; i++) {
          const isLastSlice = i === sliceLength && i > 1

          if (
            updatedAirportValues[`origAirports${i}`]?.length ===
              state.defaults?.AIRPORTS[0].originAirports.length &&
            updatedAirportValues[`destAirports${i}`]?.length ===
              state.defaults?.AIRPORTS[0].destinationAirports.length
          ) {
            updatedAirportValues[`origAirports${i}`] = []
            updatedAirportValues[`destAirports${i}`] = []
          }

          const originSet = new Set(updatedAirportValues[`origAirports${i}`])
          const destSet = new Set(updatedAirportValues[`destAirports${i}`])

          // choose origin or destination unless it's the last slice, in which case, chose the opposite.
          const activeSet =
            action.entity === 'origin'
              ? isLastSlice
                ? destSet
                : originSet
              : isLastSlice
              ? originSet
              : destSet

          if (meta.isOnlyClick) {
            activeSet.clear()
            activeSet.add(airportCode)
          } else {
            if (shouldAdd) {
              activeSet.add(airportCode)
            } else {
              activeSet.delete(airportCode)
            }
          }

          updatedAirportValues[`origAirports${i}`] = [...originSet.values()]
          latestFilterChange[`origAirports${i}`] = [...originSet.values()]
          updatedAirportValues[`destAirports${i}`] = [...destSet.values()]
          latestFilterChange[`destAirports${i}`] = [...destSet.values()]
        }
      }

      return {
        ...state,
        latestFilterChange,
        values: updatedAirportValues,
      }

    case UPDATE_EXACT_DATE_FILTERS_REQ:
      const [dateKey, dateKeyValue] = payload
      const prevExcludedDates = (prevValues && prevValues.excludedDates) || []
      const excludedDatesSet = new Set(prevExcludedDates.slice())

      if (dateKeyValue) {
        excludedDatesSet.delete(dateKey)
      } else {
        excludedDatesSet.add(dateKey)
      }

      const excludedDates = [...excludedDatesSet.values()]

      return {
        ...state,
        latestFilterChange: { excludedDates: { [dateKey]: dateKeyValue } },
        values: Object.assign({}, prevValues, { excludedDates }),
      }

    /**
     * If this is not refactored after SBS test, its gonna be a NSFW
     */
    case SET_INITIAL_FILTER_INFO:
      let initialValuesObj = {}
      common.DEFAULTS = payload
      const initialIncludedCarriers = payload.AIRLINES.map(
        airline => airline.value
      )

      if (sbsParams.isSBS) {
        const sliceIndex = sbsParams.activeTabId || defaultSelectedTab
        const stopsSelected = 'selectedStops' + sliceIndex

        /**
         * 'prevTab' is available when user transition from 1 tab to another and
         *  select the same itin. At this point SET_INITIAL_FILTER_INFO is called with filter values from cache
         * as a new airSearch call is not made
         */
        if (sbsParams.prevTab) {
          common.DEFAULTS = payload.filterDefaults
          initialValuesObj = prevValues
          const time = payload?.TIME?.[0]
          const defaultTime = payload?.DEFAULT_TIME?.[0]
          if (time && defaultTime) {
            if (
              time.minTakeoffTime !== defaultTime.minTakeoffTime ||
              time.maxTakeoffTime !== defaultTime.maxTakeoffTime
            ) {
              initialValuesObj[`departTimeStart1`] = time.minTakeoffTime
              initialValuesObj[`departTimeEnd1`] = time.maxTakeoffTime
            }
            if (
              time.minLandingTime !== defaultTime.minLandingTime ||
              time.maxLandingTime !== defaultTime.maxLandingTime
            ) {
              initialValuesObj[`arriveTimeStart1`] = time.minLandingTime
              initialValuesObj[`arriveTimeEnd1`] = time.maxLandingTime
            }
            if (time.sliceDurationMax !== time.inputDurationValue) {
              const newDuration = Math.round(
                time.inputDurationValue / 60 / 1000
              )
              initialValuesObj[`sliceDurationMax1`] = newDuration
            }
          }
        } else {
          // Reset originalValues for Return Tab
          initialValuesObj = Object.assign({}, prevValues, {
            selectedStops2: {
              0: true,
              1: true,
              7: true,
            },
            numOfStops1: '7',
            numOfStops2: '7',
            includedCarriers2: [],
            excludedCarriers2: [],
            sliceDurationMin2: null,
            sliceDurationMax2: null,
            departTimeStart2: null,
            departTimeEnd2: null,
            arriveTimeStart2: null,
            arriveTimeEnd2: null,
            destAirports2: null,
            origAirports2: null,
            sliceDurationMin1: null,
            sliceDurationMax1: null,
            departTimeStart1: null,
            departTimeEnd1: null,
            arriveTimeStart1: null,
            arriveTimeEnd1: null,
            attributes1: [],
            attributes2: [],
          })

          payload.NUM_OF_STOPS.forEach(stop => {
            initialValuesObj[stopsSelected][stop.value] = stop.checked
            if (stop.checked) {
              initialValuesObj['numOfStops' + sliceIndex] = stop.value
            }
          })

          initialValuesObj = Object.assign(initialValuesObj, {
            [`includedCarriers${sliceIndex}`]: initialIncludedCarriers,
          })

          initialValuesObj = Object.assign(initialValuesObj, {
            [`excludedCarriers${sliceIndex}`]: [],
          })

          payload.AIRPORTS.forEach(airportObj => {
            initialValuesObj[
              'destAirports' + sliceIndex
            ] = airportObj.destinationAirports.map(airport => airport.value)
            initialValuesObj[
              'origAirports' + sliceIndex
            ] = airportObj.originAirports.map(airport => airport.value)
          })
        }
      } else {
        // some stops could be disabled based on search results but the model object doesn't know about that.
        payload.NUM_OF_STOPS.forEach(stop => {
          prevValues.selectedStops[stop.value] = stop.checked
          if (stop.checked) {
            prevValues.numOfStops = stop.value
          }
        })
        initialValuesObj = Object.assign({}, cloneDeep(prevValues), {
          includedCarriers: initialIncludedCarriers,
          excludedCarriers: [],
          sliceDurationMin1: null,
          sliceDurationMax1: null,
          departTimeStart1: null,
          departTimeEnd1: null,
          arriveTimeStart1: null,
          arriveTimeEnd1: null,
        })
        payload.AIRPORTS.forEach((airportObj, index) => {
          const sliceId = index + 1
          initialValuesObj[
            'destAirports' + sliceId
          ] = airportObj.destinationAirports.map(airport => airport.value)
          initialValuesObj[
            'origAirports' + sliceId
          ] = airportObj.originAirports.map(airport => airport.value)
        })
      }

      state.lastReset = Date.now()

      return {
        ...state,
        values: initialValuesObj,
        defaults: common.DEFAULTS,
        originalValues: initialValuesObj,
      }
    case UPDATE_INCLUDED_CARRIERS:
      const includedAirlines = {
        includedCarriers: payload.split(','),
      }
      return {
        ...state,
        latestFilterChange,
        values: Object.assign({}, prevValues, includedAirlines),
      }
    case SET_CURRENT_FILTER_INFO:
      // fires when mobile overlay is closed without applying changes
      return {
        ...state,
        latestFilterChange: payload.filterInfo.latestFilterChange,
        values: payload.filterInfo.values,
      }
    default:
      return state
  }
}
